// src/components/finance/FinanceLedger.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}
function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}
function fmtMoney(v) {
  const n = Number(v || 0)
  return `${n.toLocaleString('fr-FR')} XOF`
}

export default function FinanceLedger() {
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [accounts, setAccounts] = useState([])
  const [accountCode, setAccountCode] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const loadAccounts = useCallback(async () => {
    const data = await apiFetch(`/api/finance/accounts?from=${from}&to=${to}`)
    setAccounts(data?.rows || [])
    if (!accountCode && data?.rows?.[0]?.code) {
      setAccountCode(data.rows[0].code)
    }
  }, [from, to, accountCode])

  const loadLedger = useCallback(async () => {
    if (!accountCode) return
    setLoading(true)
    setErr(null)
    try {
      const params = new URLSearchParams()
      params.set('from', from)
      params.set('to', to)
      params.set('account_code', accountCode)
      const data = await apiFetch(`/api/finance/ledger?${params.toString()}`)
      setRows(data?.rows || [])
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur chargement grand livre'))
    } finally {
      setLoading(false)
    }
  }, [from, to, accountCode])

  useEffect(() => { loadAccounts() }, [loadAccounts])
  useEffect(() => { loadLedger() }, [loadLedger])

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Grand livre</h1>
        <div className="text-sm text-slate-500 mt-1">
          Période: <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
        </div>
      </div>

      <FinanceNav />

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 pilot-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 pilot-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="md:col-span-4">
            <label className="text-xs font-medium text-slate-500 block mb-1">Compte</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white pilot-input"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-slate-900 text-white w-full"
              onClick={loadLedger}
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
          {err}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Journal</div>
          <div className="col-span-4">Référence</div>
          <div className="col-span-2 text-right">Débit</div>
          <div className="col-span-2 text-right">Crédit</div>
        </div>
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Chargement…</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <div key={`${r.entry_date}-${i}`} className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-slate-50">
                <div className="col-span-2 text-sm">{fmtDateFR(r.entry_date)}</div>
                <div className="col-span-2 text-sm">{r.journal_code || '—'}</div>
                <div className="col-span-4 text-sm truncate">{r.ref || r.memo || '—'}</div>
                <div className="col-span-2 text-right text-sm">{fmtMoney(r.debit)}</div>
                <div className="col-span-2 text-right text-sm">{fmtMoney(r.credit)}</div>
              </div>
            ))}
            {!rows.length && (
              <div className="p-4 text-sm text-slate-500">Aucun mouvement.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
